/**
 * B-GTCC-SVC-RULES-WIRE1 — wires the Cockpit "Rules" tab to
 * `GET /v1/admin/gtcc/rules/summary` (`../../../api/cockpit-svc.ts`) instead
 * of `COCKPIT_TAB_FIXTURES`. Self-fetching, no data props from
 * `GtccCockpitView` — same shape as `InfraTab.tsx`/`SvcTab.tsx`.
 *
 * Per-rule detail (drawer contents) has no server source yet, see
 * `docs/design/gtcc-screens/B-GTCC-SVC-RULES-WIRE1/ABGLEICH.md`; the drawer
 * always shows "Keine feuernde Regel messbar." — not a UI bug.
 */
import { Drawer } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import {
  fetchCockpitRulesSummary,
  type CockpitRuleGroupSummary,
  type CockpitRulesSummaryDto,
} from "../../../api/cockpit-svc.js";
// Same identifier as `svc.ts`'s own `useQuery` call: the pinned key-reader
// (`gtcc-invalidation-targets.test.ts`) resolves module constants by name
// across all files, so importing the literal's own name here — rather than
// the `COCKPIT_`-prefixed alias, which is an identifier reference, not an
// array literal — keeps this key statically readable.
import { RULES_SUMMARY_KEY } from "../../../api/svc.js";
import { Panel, Row, Tile, TilesGrid } from "../../../ui/primitives.js";
import { CockpitFreshBadge, CockpitGate } from "./CockpitGtccGate.js";

/* ---------------------------------------------------------------- query */

/** Ticket floor of the cockpit: never refetch faster than 30 s. */
const RULES_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/* ---------------------------------------------------------------- tiles */

function SummaryTiles(props: { readonly data: CockpitRulesSummaryDto }): ReactNode {
  const { targets, scrapeErrors24h } = props.data;
  const stale = targets.stale[0];
  return (
    <>
      <Tile tone="ok" title="TARGETS GESUND" value={targets.healthy} />
      <Tile tone="crit" title="DOWN" value={targets.down} />
      <Tile
        tone="warn"
        title="STALE"
        value={targets.stale.length}
        statusTone="warn"
        status={
          stale === undefined
            ? undefined
            : `${stale.name} · ${Math.round(stale.ageSeconds / 60)} min`
        }
      />
      <Tile tone="info" title="SCRAPE-FEHLER 24 H" value={scrapeErrors24h} />
    </>
  );
}

/* ------------------------------------------------------------------ tab */

export function RulesTab(): ReactNode {
  const { t } = useLocale();
  const query = useQuery({
    queryKey: RULES_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchCockpitRulesSummary(signal),
    ...RULES_QUERY_OPTIONS,
  });
  const data = query.data !== undefined && query.data.ok ? query.data.data : null;

  const [selectedName, setSelectedName] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("ruleGroup");
  });
  const openGroup = data?.groups.find((group) => group.name === selectedName) ?? null;
  const setGroup = (group: CockpitRuleGroupSummary | null) => {
    setSelectedName(group?.name ?? null);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (group === null) url.searchParams.delete("ruleGroup");
    else url.searchParams.set("ruleGroup", group.name);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  };

  return (
    <div data-testid="gtcc-cockpit-rules-tab">
      <TilesGrid testId="gtcc-cockpit-rules-tiles">
        <CockpitGate
          query={query}
          testId="gtcc-cockpit-rules-summary"
          render={(summary) => <SummaryTiles data={summary} />}
        />
      </TilesGrid>
      <Panel
        title={
          <>
            Regel-Gruppen{" "}
            {data === null ? null : (
              <CockpitFreshBadge
                seconds={data.freshnessSeconds}
                measuredAt={data.measuredAt}
                stale={data.status === "stale"}
              />
            )}
          </>
        }
        badge={
          <span className="mono muted">
            Fokus: aktive/problematische zuerst · Suche · Domänen-Filter
          </span>
        }
      >
        <CockpitGate
          query={query}
          testId="gtcc-cockpit-rules-groups"
          render={(summary) => (
            <>
              {summary.groups.map((group) => (
                <button
                  type="button"
                  key={group.name}
                  onClick={() => setGroup(group)}
                  className="gtcc-rule-group"
                >
                  <Row
                    tone={group.verdict}
                    value={`${group.rules} Regeln · ${
                      group.firing === 0
                        ? "ruhig"
                        : t("admin.gtcc.cockpit.rules.firing", { count: group.firing })
                    }`}
                  >
                    <strong>{group.name}</strong>
                  </Row>
                </button>
              ))}
            </>
          )}
        />
        <p className="mono muted">
          Kein Endlos-Inventar: Gruppen öffnen sich per Klick in eine Detail-Schublade — die
          Diagnose-Fläche bleibt oben.
        </p>
      </Panel>
      <Drawer
        open={openGroup !== null}
        onClose={() => setGroup(null)}
        ariaLabel={openGroup?.name ?? "Regel-Gruppe"}
      >
        <div className="gtcc-rule-drawer">
          <h2>{openGroup?.name}</h2>
          <p className="mono muted">Keine feuernde Regel messbar.</p>
        </div>
      </Drawer>
    </div>
  );
}
